import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Database, 
  HardDrive, 
  Download, 
  Upload, 
  Trash2, 
  RefreshCw, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Archive
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const BackupManager = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);

  const fetchBackups = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/backup/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setBackups(response.data.data);
      }
    } catch (err) {
      setError('Gagal memuat daftar backup');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBackups();
  }, [fetchBackups]);

  const createBackup = async (type = 'all') => {
    try {
      setCreating(true);
      setError(null);
      setSuccess(null);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/v2/backup/create`, 
        { type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setSuccess(`Backup berhasil dibuat! ${response.data.results.length} file tersimpan.`);
        fetchBackups();
      }
    } catch (err) {
      setError('Gagal membuat backup');
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const handleRestore = async (filename) => {
    if (!confirmRestore || confirmRestore !== filename) {
      setConfirmRestore(filename);
      return;
    }

    try {
      setRestoring(filename);
      setError(null);
      setSuccess(null);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/v2/backup/restore`,
        { filename, confirm: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setSuccess(response.data.message);
        if (response.data.pre_restore_backup) {
          setSuccess(prev => prev + ` Backup sebelum restore: ${response.data.pre_restore_backup}`);
        }
        fetchBackups();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal restore backup');
      console.error(err);
    } finally {
      setRestoring(null);
      setConfirmRestore(null);
    }
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm(`Yakin ingin menghapus ${filename}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/v2/backup/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(`File ${filename} berhasil dihapus`);
      fetchBackups();
    } catch (err) {
      setError('Gagal menghapus backup');
      console.error(err);
    }
  };

  const getTypeIcon = (type) => {
    if (type === 'application') return <Archive className="w-5 h-5 text-purple-500" />;
    if (type.includes('database')) return <Database className="w-5 h-5 text-blue-500" />;
    return <HardDrive className="w-5 h-5 text-gray-500 dark:text-slate-400" />;
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'application': return 'Aplikasi';
      case 'database-radius': return 'DB Radius';
      case 'database-master': return 'DB Master';
      case 'database-tenant': return 'DB Tenant';
      default: return 'Database';
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatSize = (sizeMb) => {
    if (sizeMb >= 1024) return `${(sizeMb / 1024).toFixed(2)} GB`;
    return `${sizeMb.toFixed(2)} MB`;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6 max-w-6xl mx-auto" data-testid="backup-manager">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <HardDrive className="w-7 h-7" />
          Backup Manager
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Kelola backup database dan aplikasi untuk melindungi data Anda
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center gap-2 dark:bg-red-900/40 dark:text-red-300">
          <AlertTriangle className="w-5 h-5" />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center gap-2 dark:bg-green-900/40 dark:text-green-300">
          <CheckCircle className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Action Buttons */}
      <div className="mb-6 flex flex-wrap gap-3">
        <button
          onClick={() => createBackup('all')}
          disabled={creating}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-all"
          data-testid="create-full-backup-btn"
        >
          {creating ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
          {creating ? 'Membuat Backup...' : 'Full Backup (DB + App)'}
        </button>
        <button
          onClick={() => createBackup('database')}
          disabled={creating}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-all"
          data-testid="create-db-backup-btn"
        >
          <Database className="w-5 h-5" />
          Backup Database Saja
        </button>
        <button
          onClick={() => createBackup('application')}
          disabled={creating}
          className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 transition-all"
          data-testid="create-app-backup-btn"
        >
          <Archive className="w-5 h-5" />
          Backup Aplikasi Saja
        </button>
        <button
          onClick={fetchBackups}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 transition-all"
          data-testid="refresh-backups-btn"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Warning */}
      <div className="mb-6 p-4 bg-yellow-50 border border-yellow-300 rounded-lg dark:bg-yellow-900/30">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 dark:text-yellow-400" />
          <div>
            <p className="font-semibold text-yellow-800">Perhatian!</p>
            <p className="text-yellow-700 text-sm dark:text-yellow-300">
              Restore akan menimpa data/aplikasi yang ada. Pastikan Anda sudah membuat backup terbaru sebelum melakukan restore.
              Untuk restore aplikasi, sistem akan otomatis membuat backup sebelum menimpa.
            </p>
          </div>
        </div>
      </div>

      {/* Backup List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Tipe</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Nama File</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Ukuran</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Tanggal</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-200">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-500" />
                    <p className="mt-2 text-gray-500 dark:text-slate-400">Memuat daftar backup...</p>
                  </td>
                </tr>
              ) : backups.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500 dark:text-slate-400">
                    Belum ada backup tersedia
                  </td>
                </tr>
              ) : (
                backups.map((backup, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getTypeIcon(backup.type)}
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {getTypeLabel(backup.type)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-800 dark:text-gray-200 font-mono">
                        {backup.filename}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {formatSize(backup.size_mb)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                        <Clock className="w-4 h-4" />
                        {formatDate(backup.created_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        {confirmRestore === backup.filename ? (
                          <>
                            <button
                              onClick={() => handleRestore(backup.filename)}
                              disabled={restoring === backup.filename}
                              className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 disabled:opacity-50"
                              data-testid={`confirm-restore-${idx}`}
                            >
                              {restoring === backup.filename ? 'Restoring...' : 'Konfirmasi'}
                            </button>
                            <button
                              onClick={() => setConfirmRestore(null)}
                              className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400 dark:text-slate-200"
                            >
                              Batal
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleRestore(backup.filename)}
                              className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors dark:text-green-400 dark:hover:bg-green-900/40"
                              title="Restore"
                              data-testid={`restore-${idx}`}
                            >
                              <Upload className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => deleteBackup(backup.filename)}
                              className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors dark:text-red-400 dark:hover:bg-red-900/40"
                              title="Hapus"
                              data-testid={`delete-${idx}`}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
        <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Tips Penggunaan:</h3>
        <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1 list-disc list-inside">
          <li>Buat <strong>Full Backup</strong> sebelum update besar atau sebelum agent baru mulai bekerja</li>
          <li>Backup <strong>Database saja</strong> untuk menyimpan data pelanggan dan konfigurasi</li>
          <li>Backup <strong>Aplikasi saja</strong> untuk menyimpan kode dan file static</li>
          <li>Restore aplikasi akan otomatis membuat backup sebelum menimpa (untuk keamanan)</li>
        </ul>
      </div>
    </div>
  );
};

export default BackupManager;
